import { create } from 'zustand';

/**
 * Universele filterbalk van Menu 4 (DATA). De gekozen periode blijft staan
 * bij het wisselen tussen de vijf submappen en voedt ze allemaal.
 *
 *   jaar      → 1 jan t/m 31 dec
 *   kwartaal  → Q1..Q4 binnen het gekozen jaar
 *   maand     → 1 kalendermaand binnen het gekozen jaar
 *   bereik    → vrije kalender-picker
 */
export type PeriodeModus = 'jaar' | 'kwartaal' | 'maand' | 'bereik';

interface DataPeriodeState {
  modus: PeriodeModus;
  jaar: number;
  kwartaal: 1 | 2 | 3 | 4;
  maand: number; // 1..12
  van: string;
  tot: string;
  kiesJaar: (jaar: number) => void;
  kiesKwartaal: (kwartaal: 1 | 2 | 3 | 4 | null) => void;
  kiesMaand: (maand: number | null) => void;
  kiesBereik: (van: string, tot: string) => void;
}

const nu = new Date();
const pad = (n: number) => String(n).padStart(2, '0');
const laatsteDag = (jaar: number, maand: number) => new Date(jaar, maand, 0).getDate();

export const useDataPeriode = create<DataPeriodeState>((set) => ({
  modus: 'jaar',
  jaar: nu.getFullYear(),
  kwartaal: (Math.floor(nu.getMonth() / 3) + 1) as 1 | 2 | 3 | 4,
  maand: nu.getMonth() + 1,
  van: `${nu.getFullYear()}-01-01`,
  tot: `${nu.getFullYear()}-12-31`,
  kiesJaar: (jaar) => set((s) => ({ jaar, modus: s.modus === 'bereik' ? 'jaar' : s.modus })),
  kiesKwartaal: (kwartaal) => set(() => (kwartaal ? { kwartaal, modus: 'kwartaal' } : { modus: 'jaar' })),
  kiesMaand: (maand) => set(() => (maand ? { maand, modus: 'maand' } : { modus: 'jaar' })),
  kiesBereik: (van, tot) => set(() => ({ van, tot, modus: 'bereik' })),
}));

/** De periode als { van, tot } (inclusief), klaar voor de API. */
export function periodeVan(s: Pick<DataPeriodeState, 'modus' | 'jaar' | 'kwartaal' | 'maand' | 'van' | 'tot'>) {
  if (s.modus === 'bereik') return { van: s.van, tot: s.tot };
  if (s.modus === 'kwartaal') {
    const start = (s.kwartaal - 1) * 3 + 1;
    return { van: `${s.jaar}-${pad(start)}-01`, tot: `${s.jaar}-${pad(start + 2)}-${laatsteDag(s.jaar, start + 2)}` };
  }
  if (s.modus === 'maand') {
    return { van: `${s.jaar}-${pad(s.maand)}-01`, tot: `${s.jaar}-${pad(s.maand)}-${laatsteDag(s.jaar, s.maand)}` };
  }
  return { van: `${s.jaar}-01-01`, tot: `${s.jaar}-12-31` };
}

/** Hook: de actuele periode + een stabiele query-key. */
export function usePeriode() {
  const s = useDataPeriode();
  const p = periodeVan(s);
  return { periode: p, key: [p.van, p.tot] as const };
}
